import type { NotificationTriggerType } from '@cims-online/shared'

type TriggerMetadata = {
  readonly article: 'a' | 'an' | null
  readonly clause: string
  readonly description: string
  readonly label: string
}

const TRIGGER_METADATA: Record<NotificationTriggerType, TriggerMetadata> = {
  row_edited: {
    article: 'a',
    clause: 'workflow record is edited',
    label: 'Row Edited',
    description:
      'Triggered when a user saves changes in the edit modal opened from a workflow row’s Edit action',
  },
  row_comments_changed: {
    article: 'a',
    clause: 'workflow row comment is added, edited, or removed',
    label: 'Row Comments Changed',
    description:
      'Triggered when a user adds, edits, or removes a workflow row comment',
  },
  advanced: {
    article: 'a',
    clause: 'workflow record is advanced',
    label: 'Advanced',
    description:
      'Triggered when a workflow record is advanced to a later status',
  },
  reverted: {
    article: 'a',
    clause: 'workflow record is reverted',
    label: 'Reverted',
    description:
      'Triggered when a workflow record is reverted to an earlier status',
  },
  removed: {
    article: 'a',
    clause: 'workflow record is removed',
    label: 'Removed',
    description: 'Triggered when a workflow record is removed',
  },
  restored: {
    article: 'a',
    clause: 'workflow record is restored',
    label: 'Restored',
    description: 'Triggered when a removed workflow record is restored',
  },
  option_state_changed: {
    article: 'an',
    clause: 'approval option’s state is changed',
    label: 'Option State Changed',
    description:
      'Triggered when an approval option’s state is changed on a workflow record',
  },
}

function isKnownTrigger(trigger: string): trigger is NotificationTriggerType {
  return Object.prototype.hasOwnProperty.call(TRIGGER_METADATA, trigger)
}

function humanizeTrigger(trigger: string) {
  return trigger
    .split('_')
    .filter(Boolean)
    .map((part) => `${part[0]?.toUpperCase() ?? ''}${part.slice(1)}`)
    .join(' ')
}

/**
 * Returns compact display and natural-language sentence metadata for a
 * notification trigger.
 */
export function triggerMetadata(
  trigger: NotificationTriggerType
): TriggerMetadata {
  return TRIGGER_METADATA[trigger]
}

/**
 * Returns known trigger metadata labels and humanizes unknown identifiers so
 * version-skewed API responses remain displayable.
 */
export function triggerLabel(trigger: string) {
  if (isKnownTrigger(trigger)) return triggerMetadata(trigger).label
  return humanizeTrigger(trigger)
}
